import { ArenaHomeTile } from "@/components/ArenaHomeTile";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { NAV_SECTIONS } from "@/lib/nav-sections";
import { Coffee, Users, User, MessageSquare, ChevronRight, Calendar, CheckCircle2, Circle, Sparkles, Gem, Stethoscope, Briefcase, ShieldCheck, Contact, Home, TrendingUp, Coins, ShoppingBag, Orbit, MapPin, FileCheck, UserPlus, Handshake, type LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { type Profile } from "@/lib/people-constants";
import { useResidentContribution } from "@/lib/drives";
import { formatEventDate, type EventRow } from "@/routes/events";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import type { CircleRow } from "./circles";
import { CoffeeFollowUpPrompt, usePendingCoffeeFollowUps } from "@/components/CoffeeFollowUpPrompt";
import { GrowthReminder } from "@/components/NextStep";
import { FirstSteps } from "@/components/FirstSteps";
import { EventPhotoReportPrompt, useEventPhotoCandidates } from "@/components/EventPhotoReportPrompt";
import { ActivityReportFeed } from "@/components/ActivityReportFeed";
import { profileCoreProgress } from "@/lib/profile-progress";
import { sharedCount, toInterestSet } from "@/lib/match-people";
import { MatchesForYou } from "@/components/MatchesForYou";
import { fetchPeople } from "@/lib/match-people";
import { expertCategoriesOf, isExpert } from "@/lib/expert";
import { buildInviteLink, inviteShareText, pluralPeople } from "@/lib/referral";
import { toast } from "sonner";
import { bannersTable, type HomeBanner } from "@/lib/home-banners";
import { rankHomeEvents, type RankedHomeEvent } from "@/lib/home-events";




export const Route = createFileRoute("/_authenticated/home")({
  head: () => ({ meta: [
    { title: "Главная — TurkeyDrive" },
    { name: "description", content: "Твои события, знакомства и сообщества в TurkeyDrive." },
    { property: "og:title", content: "Главная — TurkeyDrive" },
    { property: "og:description", content: "Твои события, знакомства и сообщества в TurkeyDrive." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: HomePage,
});

function useWelcomeRedirect(profile: Profile | undefined) {
  const navigate = useNavigate();
  useEffect(() => {
    if (profile && profile.has_seen_welcome === false) {
      navigate({ to: "/profile", replace: true });
    }
  }, [profile, navigate]);
}

type RequestRow = {
  id: string;
  user_id: string;
  title: string;
  category: string;
  city: string;
  description: string | null;
  request_type: string | null;
  contact_link: string | null;
  status: string;
  created_at: string | null;
};

type PartnerTile = {
  key: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  badge: string;
  href: string;
  rel: string;
  modal?: { title: string; lead: string; disclaimer: string };
};

const PARTNER_TILES: PartnerTile[] = [
  {
    key: "medical-drive",
    icon: Stethoscope,
    title: "Медицина",
    subtitle: "Подбор клиники и врача",
    badge: "Партнёр",
    href: "https://wa.me/905321586076?text=%D0%97%D0%B4%D1%80%D0%B0%D0%B2%D1%81%D1%82%D0%B2%D1%83%D0%B9%D1%82%D0%B5!%20%D0%9F%D0%B8%D1%88%D1%83%20%D1%81%20%D0%BF%D0%BB%D0%B0%D1%82%D1%84%D0%BE%D1%80%D0%BC%D1%8B%20TurkeyDrive%20%D0%BF%D0%BE%20%D0%B2%D0%BE%D0%BF%D1%80%D0%BE%D1%81%D1%83%20%D0%BC%D0%B5%D0%B4%D0%B8%D1%86%D0%B8%D0%BD%D1%8B%20(%D0%BF%D0%BE%D0%B4%D0%B1%D0%BE%D1%80%20%D0%BA%D0%BB%D0%B8%D0%BD%D0%B8%D0%BA%D0%B8%2F%D0%B2%D1%80%D0%B0%D1%87%D0%B0).",
    rel: "noopener noreferrer",
    modal: {
      title: "Медицина — подбор клиники и врача",
      lead: "Поможем подобрать клинику и врача, запишем на приём.",
      disclaimer:
        "Услугу оказывает независимый партнёр Алла Чардаклы. TurkeyDrive связывает вас с партнёром и не отвечает за оказанные им услуги.",
    },
  },
  {
    key: "kristal",
    icon: Home,
    title: "Жильё",
    subtitle: "Аренда и покупка",
    badge: "Партнёр",
    href: "https://wa.me/905522309693?text=%D0%97%D0%B4%D1%80%D0%B0%D0%B2%D1%81%D1%82%D0%B2%D1%83%D0%B9%D1%82%D0%B5!%20%D0%9F%D0%B8%D1%88%D1%83%20%D1%81%20%D0%BF%D0%BB%D0%B0%D1%82%D1%84%D0%BE%D1%80%D0%BC%D1%8B%20TurkeyDrive%20%D0%BF%D0%BE%20%D0%B2%D0%BE%D0%BF%D1%80%D0%BE%D1%81%D1%83%20%D0%B6%D0%B8%D0%BB%D1%8C%D1%8F%20(%D0%B0%D1%80%D0%B5%D0%BD%D0%B4%D0%B0%2F%D0%BF%D0%BE%D0%BA%D1%83%D0%BF%D0%BA%D0%B0).",
    rel: "noopener noreferrer",
  },
  {
    key: "lift",
    icon: TrendingUp,
    title: "Рост бизнеса",
    subtitle: "Система, продажи, AI и масштабирование",
    badge: "от TurkeyDrive",
    href: "https://lift-growthsystem.netlify.app/?utm_source=turkeydrive&utm_medium=partner&utm_campaign=lift_card",
    rel: "noopener",
  },
  {
    key: "investments",
    icon: Coins,
    title: "Инвестиции",
    subtitle: "Подбор объектов и сопровождение",
    badge: "Партнёр",
    href: "https://wa.me/79175716819?text=%D0%97%D0%B4%D1%80%D0%B0%D0%B2%D1%81%D1%82%D0%B2%D1%83%D0%B9%D1%82%D0%B5%21%20%D0%9F%D0%B8%D1%88%D1%83%20%D0%B8%D0%B7%20TurkeyDrive%2C%20%D0%B8%D0%BD%D1%82%D0%B5%D1%80%D0%B5%D1%81%D1%83%D0%B5%D1%82%20%D0%BD%D0%B0%D0%BF%D1%80%D0%B0%D0%B2%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5%20%C2%AB%D0%98%D0%BD%D0%B2%D0%B5%D1%81%D1%82%D0%B8%D1%86%D0%B8%D0%B8%C2%BB.%20%D0%9C%D0%B5%D0%BD%D1%8F%20%D0%B7%D0%BE%D0%B2%D1%83%D1%82%3A%20",
    rel: "noopener noreferrer",
    modal: {
      title: "Инвестиции — подбор объектов и сопровождение",
      lead: "Поможем подобрать объект и сопроводить сделку.",
      disclaimer:
        "Услугу оказывает независимый партнёр Кристина Таланкина. TurkeyDrive связывает вас с партнёром и не отвечает за оказанные им услуги.",
    },
  },
  {
    key: "citizenship",
    icon: FileCheck,
    title: "Гражданство Турции",
    subtitle: "Оформление и сопровождение",
    badge: "Партнёр",
    href: "https://wa.me/79175716819?text=" + encodeURIComponent("Здравствуйте! Пишу из TurkeyDrive, интересует направление «Гражданство Турции». Меня зовут: "),
    rel: "noopener noreferrer",
    modal: {
      title: "Гражданство Турции — оформление и сопровождение",
      lead: "Поможем с оформлением гражданства Турции и сопровождением на всех этапах.",
      disclaimer:
        "Услугу оказывает независимый партнёр Кристина Таланкина. TurkeyDrive связывает вас с партнёром и не отвечает за оказанные им услуги.",
    },
  },
];

type ServiceTile = { key: string; icon: LucideIcon; label: string; to?: string; search?: Record<string, unknown>; soon?: boolean; href?: string };

const SERVICE_TILES: ServiceTile[] = NAV_SECTIONS.map((n) => ({ key: n.key, icon: n.Icon, label: n.label, to: n.to, search: n.search, soon: n.soon, href: n.href }));

function HomePage() {
  const { user } = useAuth();
  const uid = user?.id;

  const { data: profile } = useQuery({
    queryKey: ["profile", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase.from("profiles").select("*").eq("user_id", uid!).single();
      if (error) throw error;
      return data as Profile;
    },
  });

  useWelcomeRedirect(profile);





  const { data: openCoffeeRaw = [] } = useQuery({
    queryKey: ["home", "open-coffee"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, city, photo_url, profession, coffee_status, interests, created_at")
        .eq("visible_in_directory", true)
        .contains("coffee_status", ["открыт_к_знакомствам"])
        .order("created_at", { ascending: false })
        .limit(40);
      if (error) throw error;
      return (data ?? []) as Profile[];
    },
  });

  // Персонализация: среди открытых к кофе сначала те, с кем больше общих интересов.
  const openCoffee = useMemo(() => {
    const mine = toInterestSet(profile);
    const others = openCoffeeRaw.filter((p) => p.user_id !== uid);
    if (mine.size === 0) return others;
    return others
      .map((p, idx) => ({ p, idx, s: sharedCount(p, mine) }))
      .sort((a, b) => b.s - a.s || a.idx - b.idx)
      .map((x) => x.p);
  }, [openCoffeeRaw, profile, uid]);
  const myInterestSet = useMemo(() => toInterestSet(profile), [profile]);


  const { data: recent = [] } = useQuery({
    queryKey: ["home", "recent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, city, photo_url")
        .eq("visible_in_directory", true)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as Profile[];
    },
  });





  // My requests count (for progress widget)
  const { data: myRequestsCount = 0 } = useQuery({
    queryKey: ["home", "my-requests-count", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("requests")
        .select("*", { count: "exact", head: true })
        .eq("user_id", uid!);
      if (error) throw error;
      return count ?? 0;
    },
  });




  const { data: publishedCircles = [] } = useQuery({
    queryKey: ["home", "published-circles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select("id, name, logo_url, cover, city, host_user_id")
        .eq("is_deleted", false)
        .eq("status", "published")
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as CircleRow[];
    },
  });

  const circleIds = publishedCircles.map((c) => c.id);

  const { data: circleMembers = [] } = useQuery({
    queryKey: ["home", "circle-members-counts", circleIds.join(",")],
    enabled: circleIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_members")
        .select("circle_id, user_id")
        .in("circle_id", circleIds);
      if (error) throw error;
      return (data ?? []) as { circle_id: string; user_id: string }[];
    },
  });

  return (

    <div className="min-h-screen w-full max-w-full overflow-x-clip bg-white">
      <main className="mx-auto box-border w-full max-w-[1120px] space-y-10 px-5 py-8 md:px-6 md:py-12 [&_*]:box-border [&>*]:min-w-0 [&>*]:max-w-full">

        <FirstSteps fallback={<GrowthReminder withExpert />} />
        <ArenaHomeTile />

        <header className="flex w-full max-w-full items-center justify-between gap-1">
          <div className="min-w-0 flex-1">
            <h1 className="break-words text-3xl font-serif text-td-dark md:text-4xl">
              Привет{profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}!
            </h1>
            <p className="mt-2 break-words text-sm text-td-muted md:text-base">Что происходит в сообществе:</p>
          </div>
          <div className="sm:hidden">
            <MrDriveAvatar variant="home" size={108} />
          </div>
          <div className="hidden sm:block">
            <MrDriveAvatar variant="home" size={168} />
          </div>
        </header>

        <HomeBanners />

        {/* Ближайшие события */}
        <PersonalEvents profile={profile} />

        {/* Сообщества */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Orbit className="h-5 w-5 text-td-teal" />
              <h2 className="text-lg font-semibold text-td-dark">Сообщества</h2>
            </div>
            <Link to="/circles" className="text-sm text-td-teal hover:underline">Все сообщества →</Link>
          </div>
          {publishedCircles.length === 0 ? (
            <p className="rounded-2xl border border-td-line bg-white p-4 text-center text-sm text-td-muted">Сообществ пока нет — создай первое.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {publishedCircles.map((c) => {
                const count = circleMembers.filter((m) => m.circle_id === c.id).length;
                return <CircleCard key={c.id} circle={c} count={count} />;
              })}
            </div>
          )}
          <Link
            to="/circles"
            search={{ create: true }}
            className="mt-3 inline-flex min-h-10 w-full items-center justify-center rounded-full border border-td-teal px-4 text-sm font-semibold text-td-teal transition hover:bg-td-teal/10"
          >
            Создать сообщество
          </Link>
        </section>

        {/* Эксперты */}
        <ExpertsShelf />

        {/* Знакомство: подбор + открыты к кофе */}
        <section className="min-w-0 space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-td-teal" />
            <h2 className="text-lg font-semibold text-td-dark">Знакомства</h2>
          </div>
          {/* Подбор — без тех, кто уже в ленте «Открыты к кофе» */}
          <MatchesForYou me={profile} excludeUserIds={openCoffee.slice(0, 4).map((p) => p.user_id)} />

          <div className="min-w-0 overflow-hidden rounded-3xl border border-td-teal/25 bg-gradient-to-b from-td-teal/10 to-white p-4">
            <div className="mb-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-td-teal/15">
                  <Coffee className="h-4 w-4 text-td-teal" aria-hidden="true" />
                </span>
                <h3 className="text-base font-semibold text-td-dark">Открыты к кофе сегодня</h3>
              </div>
              <p className="mt-1 text-xs text-td-muted">Эти люди готовы к знакомству. Позови на кофе</p>
            </div>
            {openCoffee.length === 0 ? (
              <div className="rounded-2xl border border-td-line bg-white p-4 text-center">
                <p className="text-sm text-td-muted">Пока никого. Открой профиль для знакомств, и тебя увидят здесь</p>
                <Link
                  to="/profile"
                  className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full bg-td-teal px-4 text-sm font-medium text-white transition hover:bg-td-teal/90"
                >
                  Открыться к знакомствам
                </Link>
              </div>
            ) : (
              <div className="-mx-4 flex snap-x snap-mandatory scroll-pl-4 gap-3 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {openCoffee.slice(0, 6).map((p) => {
                  const common = sharedCount(p, myInterestSet);
                  return (
                    <Link
                      key={p.user_id}
                      to="/people/$userId"
                      params={{ userId: p.user_id }}
                      className="flex min-h-[200px] w-[140px] min-w-[140px] shrink-0 snap-start flex-col items-center rounded-2xl border border-td-line bg-white p-3 text-center shadow-sm transition hover:border-td-teal/60"
                      title={p.full_name}
                    >
                      <Avatar name={p.full_name} url={p.photo_url} size={60} />
                      <p className="mt-2 w-full truncate text-sm font-semibold text-td-dark">{p.full_name}</p>
                      {p.city && (
                        <p className="flex w-full min-w-0 items-center justify-center gap-1 text-xs text-td-muted">
                          <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">{p.city}</span>
                        </p>
                      )}
                      {p.profession?.trim() && (
                        <p className="w-full line-clamp-1 text-xs text-td-muted">{p.profession}</p>
                      )}
                      {common > 0 && (
                        <span className="mt-1.5 rounded-full bg-td-teal/10 px-2 py-0.5 text-[11px] text-td-teal">Общее: {common}</span>
                      )}
                      <span className="mt-auto inline-flex h-8 items-center justify-center rounded-full border border-td-teal px-3 pt-0 text-xs font-medium text-td-teal">
                        ☕ Познакомиться
                      </span>
                    </Link>
                  );
                })}
                <span aria-hidden="true" className="shrink-0 px-1" />
              </div>
            )}
            <div className="mt-2 flex items-center justify-between">
              <Link to="/coffee" className="inline-flex min-h-10 items-center text-sm text-td-teal hover:underline">Мои приглашения</Link>
              <Link to="/people" className="inline-flex min-h-10 items-center text-sm text-td-teal hover:underline">Смотреть всех →</Link>
            </div>
          </div>
        </section>

        {/* For you */}
        <ForYouSection />

        <ActivityReportFeed limit={4} showAllLink allowReport={false} />

        <InviteBannerRow profile={profile} />

        <PartnersShelf />
        <ServicesShelf />

        {/* Recent joiners */}
        <section>
          <div className="mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-td-teal" />
            <h2 className="text-lg font-semibold text-td-dark">Недавно присоединились</h2>
          </div>
          <div className="flex w-full max-w-full gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {recent.map((p) => (
              <Link
                key={p.user_id}
                to="/people/$userId"
                params={{ userId: p.user_id }}
                className="flex w-[132px] min-w-[132px] shrink-0 flex-col items-center rounded-2xl border border-td-line bg-white p-3 text-center transition hover:border-td-teal/60"
                title={p.full_name}
              >
                <Avatar name={p.full_name} url={p.photo_url} size={56} />
                <p className="mt-2 w-full truncate text-sm font-semibold text-td-dark">{p.full_name}</p>
                <p className="w-full truncate text-xs text-td-muted">{p.city || "\u00A0"}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function CircleCard({ circle, count }: { circle: CircleRow; count: number }) {
  return (
    <Link
      to="/circles/$circleId"
      params={{ circleId: circle.id }}
      className="flex items-center gap-3 rounded-2xl border border-td-line bg-white p-4 transition hover:border-td-teal/60"
    >
      {circle.logo_url ? (
        <img loading="lazy" decoding="async"
          src={circle.logo_url}
          alt={circle.name}
          className="h-12 w-12 shrink-0 rounded-2xl object-cover ring-1 ring-td-line"
        />
      ) : (
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-td-teal/20 to-td-soft text-2xl">
          {circle.cover}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-td-dark">{circle.name}</span>
        <span className="mt-0.5 flex items-center gap-2 text-xs text-td-muted">
          <MapPin className="h-3.5 w-3.5" />
          {circle.city ?? "Вся Турция"}
          <Users className="ml-1 h-3.5 w-3.5" />
          {count}
        </span>
      </span>
    </Link>
  );
}

function Avatar({ name, url, size = 40 }: { name: string; url: string | null; size?: number }) {

  const initials = (name ?? "")
    .split(" ")
    .map((s: string) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const style = { width: size, height: size };
  return url ? (
    <img loading="lazy" decoding="async" src={url} alt={name} style={style} className="shrink-0 rounded-full object-cover" />
  ) : (
    <div
      style={style}
      className="flex shrink-0 items-center justify-center rounded-full bg-td-soft text-sm font-semibold text-td-dark"
    >
      {initials || <User className="h-1/2 w-1/2 text-td-muted" aria-hidden="true" />}
    </div>
  );
}

type HomeEvent = EventRow & { circle_name?: string | null };
type PickedEvent = RankedHomeEvent<HomeEvent>;

const HOME_EVENTS_LIMIT = 5;

/** Персональный подбор в городе с добором ближайших событий по Турции. */
function PersonalEvents({ profile }: { profile: Profile | undefined }) {
  const uid = profile?.user_id;
  const city = profile?.city?.trim() ?? "";
  const myTopics = ((profile as unknown as { event_topics?: string[] } | undefined)?.event_topics ?? []) as string[];

  const EVENT_COLS =
    "id, title, event_date, event_end_date, city, cover_image_url, circle_id, topics, organizer_user_id, co_organizer_name";

  const { data: picked = [], isLoading } = useQuery({
    queryKey: ["home", "personal-events", uid, city, myTopics.join(","), HOME_EVENTS_LIMIT],
    enabled: !!uid,
    queryFn: async (): Promise<PickedEvent[]> => {
      if (!uid) return [];
      const now = new Date();
      const nowIso = now.toISOString();

      const [{ data: memberships }, { data: rsvps }] = await Promise.all([
        supabase.from("circle_members").select("circle_id").eq("user_id", uid),
        supabase.from("event_rsvps").select("event_id").eq("user_id", uid),
      ]);
      const myCircleIds = (memberships ?? []).map((m) => m.circle_id);
      const rsvpIds = new Set((rsvps ?? []).map((r) => r.event_id));

      const upcoming = () =>
        supabase
          .from("events")
          .select(EVENT_COLS)
          .eq("status", "published")
          .or(`event_end_date.gte.${nowIso},and(event_end_date.is.null,event_date.gte.${nowIso})`)
          .order("event_date", { ascending: true });

      const [candidateResponse, circleResponse, myCircleEventsResponse] = await Promise.all([
        city ? upcoming().eq("city", city).limit(60) : upcoming().limit(20),
        myCircleIds.length > 0
          ? supabase.from("circles").select("id, name").in("id", myCircleIds)
          : Promise.resolve({ data: [] }),
        // События своих сообществ из других городов тоже попадают в кандидаты (ранжируются ниже своего города).
        myCircleIds.length > 0
          ? upcoming().in("circle_id", myCircleIds).limit(20)
          : Promise.resolve({ data: [] }),
      ]);
      const circleNames: Record<string, string> = Object.fromEntries((circleResponse.data ?? []).map((c) => [c.id, c.name]));
      const withCircleNames = (rows: unknown): HomeEvent[] => ((rows ?? []) as HomeEvent[]).map((event) => ({
        ...event, circle_name: event.circle_id ? circleNames[event.circle_id] : null,
      }));
      const candidates = [
        ...withCircleNames(candidateResponse.data),
        ...withCircleNames(myCircleEventsResponse.data),
      ];
      const rank = () => rankHomeEvents({ events: candidates, myTopics, myCircleIds, rsvpIds, city, now, limit: HOME_EVENTS_LIMIT });
      let result = rank();
      if (city && result.length < HOME_EVENTS_LIMIT) {
        const { data } = await upcoming().neq("city", city).limit(20);
        candidates.push(...withCircleNames(data));
        result = rank();
      }

      // Если всё уже посещается — показываем ближайшие свои регистрации.
      if (result.length === 0 && rsvpIds.size > 0) {
        const { data } = await upcoming().in("id", [...rsvpIds]).limit(HOME_EVENTS_LIMIT);
        for (const event of (data ?? []) as unknown as HomeEvent[]) {
          result.push({ event, note: "Ты идёшь", matchedTopic: null });
        }
      }

      return result.slice(0, HOME_EVENTS_LIMIT)
        .sort((a, b) => new Date(a.event.event_date ?? "").getTime() - new Date(b.event.event_date ?? "").getTime());
    },
  });

  const personal = myTopics.length > 0 && picked.some(({ note }) => note?.startsWith("Из ") || note?.startsWith("Подобрано для тебя"));

  return (
    <section>
      <div className={personal ? "rounded-2xl bg-primary-50 p-4" : undefined}>
      {personal ? (
        <>
          <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-td-teal">🐾 Подобрал для тебя</h2>
          <p className="mb-3 text-xs text-td-muted">по твоим интересам{city ? ` · ${city}` : ""}</p>
        </>
      ) : (
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-td-teal" />
          <h2 className="text-lg font-semibold text-td-dark">Ближайшие события</h2>
        </div>
        <Link to="/events" className="text-sm text-td-teal hover:underline">Вся афиша →</Link>
      </div>
      )}
      {myTopics.length === 0 && (
        <div className="mb-3 rounded-2xl border border-td-teal/25 bg-td-teal/5 p-3">
          <p className="mb-2 text-sm text-td-dark">Выбери, что тебе интересно, и я буду подбирать события под тебя и сообщать о новых</p>
          <Link to="/profile" search={{ section: "interests" }} className="inline-flex rounded-full bg-td-teal px-4 py-2 text-xs font-semibold text-white">Выбрать интересы</Link>
        </div>
      )}
      {picked.length === 0 ? (
        <p className="rounded-2xl border border-td-line bg-white p-4 text-center text-sm text-td-muted">
          {isLoading ? "Загрузка…" : "Пока нет ближайших событий — добавь своё."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          {picked.map(({ event, note }) => (
            <Link
              key={event.id}
              to="/events"
              search={{ event: event.id }}
              className="flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-2.5 transition hover:border-td-teal/60"
            >
              {event.cover_image_url ? (
                <img loading="lazy" decoding="async" src={event.cover_image_url} alt={event.title} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
              ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-td-teal/20 via-td-soft to-td-teal/10">
                  <Calendar className="h-6 w-6 text-td-teal/60" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                {note && (
                  <span className="mb-0.5 block truncate text-[11px] font-medium text-td-teal">{note}</span>
                )}
                <span className="line-clamp-2 break-words text-sm font-semibold leading-snug text-td-dark [overflow-wrap:anywhere]">{event.title}</span>
                <span className="mt-0.5 block truncate text-xs text-td-muted">
                  {event.event_date && event.event_end_date && new Date(event.event_date).getTime() < Date.now() && new Date(event.event_end_date).getTime() >= Date.now()
                    ? `до ${formatEventDate(event.event_end_date)}`
                    : formatEventDate(event.event_date)} · {event.city}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
      {personal && (
        <Link to="/events" className="mt-3 flex w-full items-center justify-center rounded-xl border border-td-teal/30 bg-white py-2 text-sm font-medium text-td-teal transition hover:bg-td-teal/5">Вся афиша →</Link>
      )}
      </div>
      <Link
        to="/events/new"
        className="mt-3 inline-flex min-h-10 w-full items-center justify-center rounded-full border border-td-teal px-4 text-sm font-semibold text-td-teal transition hover:bg-td-teal/10"
      >
        Добавить событие
      </Link>
    </section>
  );
}

/** Лента экспертов: те же данные и правила видимости, что в каталоге (скрытых админом нет). */
function ExpertsShelf() {
  const { data: people = [] } = useQuery({ queryKey: ["people"], queryFn: fetchPeople, staleTime: 60_000 });
  const experts = useMemo(() => (people as Profile[]).filter((p) => isExpert(p)).slice(0, 6), [people]);
  return (
    <section className="min-w-0">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Contact className="h-5 w-5 text-td-teal" />
          <h2 className="text-lg font-semibold text-td-dark">Эксперты</h2>
        </div>
        <Link to="/people" search={{ experts: true }} className="text-sm text-td-teal hover:underline">Все эксперты →</Link>
      </div>
      {experts.length === 0 ? (
        <p className="rounded-2xl border border-td-line bg-white p-4 text-center text-sm text-td-muted">Экспертов пока нет — стань первым.</p>
      ) : (
        <div className="-mx-5 flex snap-x snap-mandatory scroll-pl-5 gap-3 overflow-x-auto px-5 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:-mx-6 md:px-6">
          {experts.map((p) => (
            <Link
              key={p.user_id}
              to="/people/$userId"
              params={{ userId: p.user_id }}
              className="flex w-[140px] min-w-[140px] shrink-0 snap-start flex-col items-center rounded-2xl border border-td-line bg-white p-3 text-center shadow-sm transition hover:border-td-teal/60"
              title={p.full_name}
            >
              <Avatar name={p.full_name} url={p.photo_url} size={60} />
              <p className="mt-2 w-full truncate text-sm font-semibold text-td-dark">{p.full_name}</p>
              {expertCategoriesOf(p)[0] && (
                <p className="w-full line-clamp-2 text-xs text-td-teal">{expertCategoriesOf(p)[0]}</p>
              )}
              {p.city && (
                <p className="mt-0.5 flex w-full min-w-0 items-center justify-center gap-1 text-xs text-td-muted">
                  <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">{p.city}</span>
                </p>
              )}
            </Link>
          ))}
          <span aria-hidden="true" className="shrink-0 px-1" />
        </div>
      )}
      <Link
        to="/profile"
        search={{ section: "services" }}
        className="mt-3 inline-flex min-h-10 w-full items-center justify-center rounded-full border border-td-teal px-4 text-sm font-semibold text-td-teal transition hover:bg-td-teal/10"
      >
        Стать экспертом
      </Link>
    </section>
  );
}

function ForYouSection() {
  const { data: eventCandidates = [] } = useEventPhotoCandidates();
  const { data: coffeePending = [] } = usePendingCoffeeFollowUps();
  const [skipped, setSkipped] = useState<string[]>([]);

  const showEvent = eventCandidates.some((c) => !skipped.includes(c.id));
  const showCoffee = !showEvent && coffeePending.length > 0;
  if (!showEvent && !showCoffee) return null;

  return (
    <section className="w-full max-w-full min-w-0">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-td-teal" />
        <h2 className="text-lg font-semibold text-td-dark">Для тебя</h2>
      </div>
      {showEvent ? (
        <EventPhotoReportPrompt
          skipped={skipped}
          onSkip={(id) => setSkipped((s) => (s.includes(id) ? s : [...s, id]))}
        />
      ) : (
        <CoffeeFollowUpPrompt />
      )}
    </section>
  );
}





function ProfileProgressCard({
  profile,
  myRequestsCount,
}: {
  profile: Profile | undefined;
  myRequestsCount: number;
}) {
  const uid = profile?.user_id;

  const { data: contribution } = useResidentContribution(uid);

  if (!profile) return null;

  const drives = contribution?.drives ?? 0;
  const referralCount = contribution?.referrals ?? 0;

  const progress = profileCoreProgress(profile);
  const profileFilled = progress.done === progress.total;
  const hasRequest = myRequestsCount > 0;
  const hasReferral = referralCount > 0;


  if (profileFilled && hasRequest && hasReferral) return null;

  return (
    <section className="rounded-2xl border border-td-teal/25 bg-td-teal/5 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-td-dark">Прогресс профиля</h2>
        {drives > 0 && (
          <span className="shrink-0 rounded-full bg-td-soft px-2 py-0.5 text-[11px] font-medium text-td-dark">
            {drives} 🔥
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-td-muted">
        <span>
          Основа профиля: {progress.done} из {progress.total}
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-td-soft">
        <div className="h-full rounded-full bg-td-teal transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
      </div>

      {progress.missing[0] && (
        <div className="mt-3">
          <p className="text-sm font-semibold text-td-dark">Добавь: {progress.missing[0].label}</p>
          <p className="mt-0.5 text-xs text-td-muted">Заполни, чтобы я подобрал тебе своих точнее 🐾</p>
          <Link
            to="/profile"
            search={{ section: progress.missing[0].key === "interests" ? "interests" : progress.missing[0].key === "contact" ? "contacts" : "basic" }}
            className="mt-2 inline-flex items-center rounded-full bg-td-teal px-4 py-2 text-xs font-semibold text-white transition hover:bg-td-teal/90"
          >
            Дополнить профиль →
          </Link>
        </div>
      )}

      <ul className="mt-4 space-y-2 border-t border-td-line/70 pt-3">
        <ProgressItem done={profileFilled} label="Профиль заполнен для подбора" to="/profile" />
        <ProgressItem done={hasRequest} label="Первый запрос оставлен" to="/requests" />
        <ProgressItem done={hasReferral} label="Пригласи своего" to="/profile" icon={UserPlus} />
      </ul>
    </section>
  );
}

function ProgressItem({ done, label, to, icon: Icon }: { done: boolean; label: string; to: string; icon?: LucideIcon }) {
  const content = (
    <span className="flex items-center gap-2.5 text-sm">
      {done ? (
        <CheckCircle2 className="h-5 w-5 shrink-0 text-td-teal" />
      ) : (
        Icon ? <Icon className="h-5 w-5 shrink-0 text-td-muted/60" /> : <Circle className="h-5 w-5 shrink-0 text-td-muted/60" />
      )}
      <span className={done ? "text-td-muted line-through" : "text-td-dark"}>{label}</span>
    </span>
  );
  return (
    <li>
      {done ? content : <Link to={to} className="block transition hover:opacity-80">{content}</Link>}
    </li>
  );
}

function InviteBannerRow({ profile }: { profile: Profile | undefined }) {
  const username = profile?.username;
  const userId = profile?.user_id;
  const { data: broughtCount = 0 } = useQuery({
    queryKey: ["referred-count", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("referred_by", userId!);
      if (error) throw error;
      return count ?? 0;
    },
  });
  if (!username) return null;

  const link = buildInviteLink(username);
  const shareText = inviteShareText(link);

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as unknown as { share?: () => Promise<void> }).share) {
      try {
        await (navigator as unknown as { share: (opts: Record<string, string>) => Promise<void> }).share({
          title: "TurkeyDrive",
          text: shareText,
          url: link,
        });
      } catch {
        // user cancelled or share failed
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-2xl bg-[#FDEBDC] p-3">
      <Handshake className="h-5 w-5 shrink-0 text-[#C25A12]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-td-dark">Сообщество растёт — приведи и ты своего</p>
        {broughtCount > 0 && (
          <p className="text-xs text-td-muted">
            Ты привёл(а): <span className="font-bold text-[#C25A12]">{broughtCount}</span> {pluralPeople(broughtCount)}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleShare}
        className="cta-solid shrink-0 rounded-full px-3 py-1.5 text-xs"
      >
        Пригласить
      </button>
    </div>
  );
}

function PartnersShelf() {
  const [modalKey, setModalKey] = useState<string | null>(null);
  const modalTile = PARTNER_TILES.find((p) => p.key === modalKey && p.modal);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      setActive(Math.round((el.scrollLeft / max) * (PARTNER_TILES.length - 1)));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section>
      <div className="w-full max-w-full overflow-hidden rounded-[26px] bg-primary-50 p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-td-dark">
          <span className="h-4 w-1 rounded-full bg-[#F5822B]" />
          Продукты и партнёры
        </h2>
        <div
          ref={scrollerRef}
          className="flex max-w-full gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
        >
        {PARTNER_TILES.map((p) => {
          const Icon = p.icon;
          const inner = (
            <>
              <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-td-teal/12 text-td-teal">
                <Icon className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="mt-1.5 block w-full truncate text-[12px] font-semibold leading-tight text-td-dark">{p.title}</span>
              <span className="block w-full truncate text-[10px] leading-tight text-td-muted">{p.subtitle}</span>
              <span
                className={
                  "mt-1 inline-block max-w-full truncate rounded-full px-1 py-px text-[8px] font-medium " +
                  (p.badge === "от TurkeyDrive"
                    ? "bg-[#FDEBDC] text-[#C25A12]"
                    : "bg-td-teal/10 text-td-teal")
                }
              >
                {p.badge}
              </span>
            </>
          );
          const cls =
            "flex w-[100px] min-w-[100px] shrink-0 snap-start flex-col items-start rounded-2xl border border-td-line/80 bg-white p-2 text-left shadow-md transition hover:border-td-teal/60 active:scale-[0.98]";
          if (p.modal) {
            return (
              <button key={p.key} type="button" onClick={() => setModalKey(p.key)} className={cls}>
                {inner}
              </button>
            );
          }
          return (
            <a key={p.key} href={p.href} target="_blank" rel={p.rel} className={cls}>
              {inner}
            </a>
          );
        })}
      </div>
      <div className="mt-2 flex justify-center gap-1.5">
        {PARTNER_TILES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full transition-colors ${i === active ? "bg-td-teal" : "bg-td-line"}`}
          />
        ))}
      </div>
      </div>


      {modalTile?.modal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={() => setModalKey(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-lg sm:rounded-3xl"
          >
            <h3 className="text-lg font-semibold text-td-dark">{modalTile.modal.title}</h3>
            <p className="mt-2 text-sm text-td-dark">{modalTile.modal.lead}</p>
            <p className="mt-3 rounded-2xl bg-td-soft p-3 text-xs leading-relaxed text-td-muted">
              {modalTile.modal.disclaimer}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <a
                href={modalTile.href}
                target="_blank"
                rel={modalTile.rel}
                onClick={() => setModalKey(null)}
                className="flex w-full items-center justify-center rounded-full bg-td-teal px-5 py-3 text-sm font-semibold text-white transition hover:bg-td-teal/90"
              >
                Оставить запрос в WhatsApp
              </a>
              <button
                type="button"
                onClick={() => setModalKey(null)}
                className="w-full rounded-full border border-td-line px-5 py-3 text-sm font-medium text-td-dark transition hover:bg-td-soft"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ServicesShelf() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const progress = el.scrollLeft / max;
      setActive(Math.round(progress * (SERVICE_TILES.length - 1)));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-td-muted">
        <Sparkles className="h-4 w-4 text-td-teal" />
        Сервисы Turkey Drive
      </h2>
      <div
        ref={scrollerRef}
        className="flex w-full max-w-full gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
      >
        {SERVICE_TILES.map((t) => {
          const Icon = t.icon;
          const inner = (
            <>
              <div
                className={
                  "flex h-14 w-14 items-center justify-center rounded-full " +
                  (t.soon ? "bg-td-soft" : "bg-td-teal")
                }
              >
                <Icon className={"h-6 w-6 " + (t.soon ? "text-td-muted" : "text-white")} />
              </div>
              <span className="line-clamp-2 min-h-[2.4em] whitespace-normal break-words text-[11px] font-medium leading-[1.2] text-td-dark">
                {t.label}
              </span>
            </>
          );
          const cls =
            "flex w-[80px] min-w-[80px] shrink-0 snap-start flex-col items-center gap-1.5 text-center transition active:scale-95" +
            (t.soon ? " opacity-55" : "");
          if (t.href) {
            return (
              <a key={t.key} href={t.href} target="_blank" rel="noopener noreferrer" className={cls}>
                {inner}
              </a>
            );
          }
          if (t.soon || !t.to) {
            return (
              <span key={t.key} className={cls} title="Скоро">
                {inner}
              </span>
            );
          }
          return (
            <Link key={t.key} to={t.to} search={t.search as never} className={cls}>
              {inner}
            </Link>
          );
        })}
      </div>
      <div className="mt-2 flex justify-center gap-1.5">
        {SERVICE_TILES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full transition-colors ${i === active ? "bg-td-teal" : "bg-td-line"}`}
          />
        ))}
      </div>
    </section>
  );
}

function HomeBanners() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const pausedRef = useRef(false);

  const { data: banners = [] } = useQuery({
    queryKey: ["home-banners"],
    queryFn: async () => {
      const { data, error } = await bannersTable()
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as HomeBanner[];
    },
  });

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || banners.length === 0) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      setActive(Math.round((el.scrollLeft / max) * (banners.length - 1)));
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [banners.length]);

  // Автопрокрутка раз в 5 секунд по кругу; пауза, пока человек взаимодействует.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || banners.length <= 1) return;

    const goToNext = () => {
      if (pausedRef.current) return;
      const cards = Array.from(el.children) as HTMLElement[];
      const nextIndex = (activeRef.current + 1) % banners.length;
      const card = cards[nextIndex];
      if (card) el.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
    };

    const intervalId = window.setInterval(goToNext, 2000);

    let resumeTimeoutId: number | undefined;
    const pause = () => {
      pausedRef.current = true;
      if (resumeTimeoutId) window.clearTimeout(resumeTimeoutId);
    };
    const scheduleResume = () => {
      if (resumeTimeoutId) window.clearTimeout(resumeTimeoutId);
      resumeTimeoutId = window.setTimeout(() => {
        pausedRef.current = false;
      }, 4000);
    };

    el.addEventListener("pointerdown", pause);
    el.addEventListener("pointerup", scheduleResume);
    el.addEventListener("pointercancel", scheduleResume);
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", scheduleResume, { passive: true });

    return () => {
      window.clearInterval(intervalId);
      if (resumeTimeoutId) window.clearTimeout(resumeTimeoutId);
      el.removeEventListener("pointerdown", pause);
      el.removeEventListener("pointerup", scheduleResume);
      el.removeEventListener("pointercancel", scheduleResume);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", scheduleResume);
    };
  }, [banners.length]);

  if (banners.length === 0) return null;


  return (
    <section>
      <div
        ref={scrollerRef}
        className={
          "flex max-w-full gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory" +
          (banners.length === 1 ? " justify-center" : "")
        }
      >
        {banners.map((b) => {
          const external = b.link_url.startsWith("http");
          return (
            <a
              key={b.id}
              href={b.link_url}
              {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={
                "relative block shrink-0 snap-start overflow-hidden rounded-[26px] shadow-md transition active:scale-[0.99] " +
                (banners.length === 1
                  ? "w-full"
                  : "w-[300px] min-w-[300px] sm:w-[420px] sm:min-w-[420px]")
              }
            >
              <div className="aspect-[16/9] w-full">
                <img decoding="async" src={b.image_url} alt={b.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                <div className="flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-base font-semibold leading-tight text-white">{b.title}</div>
                    {b.subtitle ? (
                      <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-white/85">{b.subtitle}</div>
                    ) : null}
                  </div>
                  <span className="cta-solid inline-flex shrink-0 items-center rounded-full px-4 py-1.5 text-xs font-semibold">
                    {b.cta_label}
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
      {banners.length > 1 ? (
        <div className="mt-2 flex justify-center gap-1.5">
          {banners.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${i === active ? "bg-td-teal" : "bg-td-line"}`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
